"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { useUnsavedChangesState } from "./unsaved-changes-context";

export default function Navbar() {
    const { data: session, update } = useSession();
    const router = useRouter();
    const { hasUnsavedChanges, clearAll } = useUnsavedChangesState();

    const rutas = session?.user?.rutas ?? [];
    const esAdmin = rutas.some(
    (ruta: any) =>
        ruta.roleName === "Administrador" ||
        ruta.roleName === "Super Administrador"
    );
    const [mounted, setMounted] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [modalSedeOpen, setModalSedeOpen] = useState(false);
    const [confirmarCambioSedeOpen, setConfirmarCambioSedeOpen] = useState(false);
    const [sedePendiente, setSedePendiente] = useState<any | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    const sedes = session?.user?.sedes ?? [];
    const sedeActual = session?.user?.sedeActual ?? sedes?.[0] ?? null;

    const handleSignOut = async () => {
        await signOut({ callbackUrl: "/login" });
    };

    const normalizePath = (path: string) => {
        if (!path || path === "/") return "/";
        return path.endsWith("/") ? path.slice(0, -1) : path;
    };



    
    const tieneRuta = (href: string) => {
        const target = normalizePath(href);

        return rutas.some((ruta: any) => {
            if (!ruta?.path || !ruta?.canView) return false;
            return normalizePath(ruta.path) === target;
        });
    };

    const aplicarCambioSede = async (sede: any) => {
        clearAll();

        await update({
            user: {
                ...session?.user,
                sedeActual: sede,
            },
        });

        setConfirmarCambioSedeOpen(false);
        setModalSedeOpen(false);
        setSedePendiente(null);

        window.location.reload();
    };

    const solicitarCambioSede = (sede: any) => {
        const sedeActualId = sedeActual?.id ?? sedeActual?.sede_id;
        const sedeSeleccionadaId = sede?.id ?? sede?.sede_id;

        if (sedeActualId === sedeSeleccionadaId) {
            setModalSedeOpen(false);
            return;
        }

        if (hasUnsavedChanges) {
            setSedePendiente(sede);
            setConfirmarCambioSedeOpen(true);
            return;
        }

        void aplicarCambioSede(sede);
    };

    return (
        <>
            <nav className="bg-white shadow-sm border-b">
                <div className="container mx-auto px-4 py-3 flex justify-between items-center">

                    {/* Logo */}
                    <div className="flex items-center gap-3">
                        <img
                            src="/img/rostro.png"
                            alt="Logo"
                            className="w-12 h-12 rounded object-cover"
                        />

                        <div className="leading-tight">
                            <span className="font-bold text-institucional text-xl block">
                                UJJAB
                            </span>

                            {sedeActual && (
                                <button
                                    type="button"
                                    onClick={() => setModalSedeOpen(true)}
                                    className="text-xs text-gray-500 hover:text-institucional transition"
                                >
                                    Sede actual:
                                    <span className="ml-1 font-semibold">
                                        {sedeActual.nombre ??
                                            sedeActual.name ??
                                            "Sin sede"}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Menú */}
                    <div className="hidden md:flex items-center gap-6">
                        <Link
                            href="/dashboard"
                            className="text-institucional font-medium hover:text-institucional/70 transition"
                        >
                            Inicio
                        </Link>

                        {(tieneRuta("/modulo-academico") || esAdmin)&& (
                            <Link
                                href="/modulo-academico"
                                className="text-institucional font-medium hover:text-institucional/70 transition"
                            >
                                Académico
                            </Link>
                        )}

                        {(tieneRuta("/modulo-financiero") || esAdmin)&& (
                            <Link
                                href="/modulo-financiero"
                                className="text-institucional font-medium hover:text-institucional/70 transition"
                            >
                                Financiero
                            </Link>
                        )}

                        {(tieneRuta("/modulo-administrativo") || esAdmin)&& (
                            <Link
                                href="/modulo-administrativo"
                                className="text-institucional font-medium hover:text-institucional/70 transition"
                            >
                                Administrativo
                            </Link>
                        )}
                    </div>

                    {/* Usuario */}
                    <div
                        className="relative"
                        onMouseEnter={() => setMenuOpen(true)}
                        onMouseLeave={() => setMenuOpen(false)}
                    >
                        <button
                            type="button"
                            className="flex items-center gap-3 rounded-lg px-2 py-2 transition hover:bg-gray-100"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-institucional/10 text-institucional">
                                <i className="fas fa-user text-lg"></i>
                            </div>

                            <div className="text-left leading-tight">
                                <span className="block max-w-36 truncate font-semibold text-institucional">
                                    {session?.user?.username ??
                                        session?.user?.name ??
                                        "Invitado"}
                                </span>
                                                                <span className="block max-w-36 truncate font-semibold text-xs text-institucional">

                                         {session?.user?.email ??
                                        session?.user?.email ??
                                        "Invitado"}
                                </span>

                                {sedeActual && (
                                    <span className="block max-w-36 truncate text-xs text-gray-500">
                                        {sedeActual.nombre ??
                                            sedeActual.name}
                                    </span>
                                )}
                            </div>

                            <i
                                className={`fas fa-chevron-down text-xs text-gray-500 transition-transform duration-200 ${
                                    menuOpen ? "rotate-180" : ""
                                }`}
                            ></i>
                        </button>

                        {/* Dropdown */}
                        <div
                            className={`absolute right-0 mt-2 w-52 rounded-lg bg-white shadow-lg border transition-all duration-150 z-50 ${
                                menuOpen
                                    ? "visible opacity-100 translate-y-0"
                                    : "invisible opacity-0 -translate-y-2"
                            }`}
                        >
                            <button
                                type="button"
                                className="block w-full text-left px-4 py-3 hover:bg-gray-100 transition"
                            >
                                Mi Perfil
                            </button>

                            <button
                                type="button"
                                onClick={() => setModalSedeOpen(true)}
                                className="block w-full text-left px-4 py-3 hover:bg-gray-100 transition"
                            >
                                Cambiar sede
                            </button>

                            <button
                                type="button"
                                className="block w-full text-left px-4 py-3 hover:bg-gray-100 transition"
                            >
                                Configuración
                            </button>

                            <hr />

                            <button
                                type="button"
                                onClick={handleSignOut}
                                className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition"
                            >
                                Cerrar sesión
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Modal de sedes */}
            {modalSedeOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border overflow-hidden">

                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-5 py-4">
                            <h2 className="text-lg font-bold text-institucional">
                                Seleccionar sede
                            </h2>

                            <button
                                type="button"
                                onClick={() => setModalSedeOpen(false)}
                                className="text-gray-500 hover:text-red-600 transition"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-5 space-y-3 max-h-[400px] overflow-y-auto">

                            {sedes.length > 0 ? (
                                sedes.map((sede: any) => {
                                    const isSelected =
                                        (sedeActual?.id ??
                                            sedeActual?.sede_id) ===
                                        (sede.id ?? sede.sede_id);

                                    return (
                                        <button
                                            key={
                                                sede.id ??
                                                sede.sede_id ??
                                                sede.codigo
                                            }
                                            type="button"
                                            onClick={() => solicitarCambioSede(sede)}
                                            className={`w-full rounded-lg border px-4 py-4 text-left transition-all ${
                                                isSelected
                                                    ? "border-institucional bg-institucional/10 text-institucional"
                                                    : "border-gray-200 hover:border-institucional/40 hover:bg-gray-50"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="font-semibold">
                                                        {sede.nombre ??
                                                            sede.name ??
                                                            "Sede"}
                                                    </p>

                                                    {(sede.codigo ||
                                                        sede.id) && (
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Código:{" "}
                                                            {sede.codigo ??
                                                                sede.id}
                                                        </p>
                                                    )}
                                                </div>

                                                {isSelected && (
                                                    <div className="text-institucional">
                                                        <i className="fas fa-check-circle text-lg"></i>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="rounded-lg border border-dashed p-6 text-center text-gray-500">
                                    No hay sedes disponibles para este usuario.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {confirmarCambioSedeOpen && sedePendiente && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border overflow-hidden">
                        <div className="border-b px-5 py-4">
                            <h2 className="text-lg font-bold text-red-700">Cambiar sede</h2>
                        </div>

                        <div className="p-5 space-y-4">
                            <p className="text-sm text-gray-700">
                                Hay un formulario abierto con cambios sin guardar.
                                Si cambias de sede ahora, esos cambios se perderán.
                            </p>

                            <div className="rounded-lg bg-gray-50 border px-4 py-3 text-sm text-gray-700">
                                <span className="font-semibold block mb-1">Nueva sede:</span>
                                {sedePendiente.nombre ?? sedePendiente.name ?? "Sede"}
                            </div>

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setConfirmarCambioSedeOpen(false);
                                        setModalSedeOpen(false);
                                        setSedePendiente(null);
                                    }}
                                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="button"
                                    onClick={() => aplicarCambioSede(sedePendiente)}
                                    className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
                                >
                                    Sí, cambiar sede
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}